import { useCallback, useContext, useMemo, useState } from 'react';
import { StyleSheet } from '../providers/ThemeProvider';
import Popup from '../components/atoms/Popup/Popup';
import { ThemeContent, useThemedComponent } from '../providers/ThemeProvider';
import { LoggingContext, useLogging } from '../providers/LoggingProvider';
import { useInterval } from 'usehooks-ts';
import Parse from '../parse';
import { DateTime, Duration } from 'luxon';
import i18n from '../translations';

// for testing
// const CHECK_SESSION_INTERVAL = Duration.fromObject({ seconds: 10 }); // 10 seconds
const CHECK_SESSION_INTERVAL = Duration.fromObject({
  hours: 1,
});

type UseAuthSessionDetectionProps = {
  user: Parse.User | null;
  logout: () => void;
};

export default function useAuthSessionDetection({
  user,
  logout,
}: UseAuthSessionDetectionProps) {
  const logger = useLogging(useAuthSessionDetection.name);
  const { styles } = useThemedComponent([sessionStyles]);
  const { setSessionId: setLoggingSessionId } = useContext(LoggingContext);
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  // Check Parse session expiration
  const checkSession = useCallback(async () => {
    if (!user) {
      logger.debug('No user found, skipping session check');
      return;
    }

    // double check that parse has the user
    const userStoredInParse = await Parse.User.currentAsync();
    if (!userStoredInParse) {
      logger.debug('No user found in parse, skipping session check');
      return;
    }

    // get the session
    const session = await Parse.Session.current();
    const refreshedSessionExpiration: Date = session.get('expiresAt');
    setLoggingSessionId(session.id);

    logger.debug('Session expiration', refreshedSessionExpiration);

    // check if the session is expired
    const now = DateTime.now();
    const expiry = DateTime.fromJSDate(refreshedSessionExpiration);

    // if session is expired, logout and let the user know with a popup
    if (expiry.toMillis() <= now.toMillis()) {
      logout();
      setShowSessionWarning(true);
    }
  }, [user, setLoggingSessionId, logger, logout]);

  useInterval(checkSession, CHECK_SESSION_INTERVAL.toMillis());

  /**
   * Render the inactivity warning popup
   */
  const renderSessionWarning = useMemo(() => {
    if (!showSessionWarning) return null;

    return (
      <Popup
        onClose={() => {}}
        open={showSessionWarning}
        xIcon={false}
        title={i18n.t('auth.session.title')}
        buttons={[
          {
            title: i18n.t('auth.session.button'),
            style: styles.button,
            onClick: () => {
              setShowSessionWarning(false);
            },
          },
        ]}>
        <p style={styles.message}>{i18n.t('auth.session.body')}</p>
      </Popup>
    );
  }, [showSessionWarning, styles.message, styles.button]);

  return {
    renderSessionWarning,
  };
}

const sessionStyles = (theme: ThemeContent): StyleSheet => ({
  container: {
    backgroundColor: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 20,
    gap: 20,
  },
  message: {
    textAlign: 'center',
    paddingTop: 30,
    paddingBottom: 10,
  },
  button: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.white,
    padding: '10px 20px',
    borderRadius: 5,
    cursor: 'pointer',
    border: 'none',
  },
});
